import { PriceTag } from "@/components/PriceTag";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/context/CatalogContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { listJournal } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import StockBadge from "@/components/StockBadge";
import ProgressiveImage from "@/components/ProgressiveImage";
import { isVideoUrl, cldImage, cldSrcSet, imgPos } from "@/lib/utils";

// ─── Trending product card (with quick-add CTA) ──────────────────
const TrendingCard = ({ p, priority = false }) => {
  const { t } = useT();
  const cart = useCart();
  const [flash, setFlash] = useState(false);

  const firstAvailable = (p.variants || []).find((v) => (v.stock ?? 0) > 0);
  const soldOut = !firstAvailable;

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    cart.addItem({
      productId: p.id,
      slug: p.slug,
      name: p.name,
      image: p.images?.[0],
      price: p.price,
      originalPrice: p.original_price,
      variantName: firstAvailable.name || "",
      quantity: 1,
      stock: firstAvailable.stock,
    });
    setFlash(true);
    setTimeout(() => setFlash(false), 1200);
  };

  return (
    <Link
      to={`/product/${p.slug}`}
      data-testid={TESTIDS.productCardLink(p.slug)}
      className="snap-start shrink-0 w-[74vw] sm:w-[46vw] md:w-[30vw] lg:w-[23vw] group"
    >
      <div className="rounded-card aspect-[3/4] overflow-hidden relative">
        <div className="absolute top-3 left-3 z-10 flex flex-col items-start gap-1.5">
          {p.trending && (
            <div
              data-testid={TESTIDS.productCardTrending(p.slug)}
              className="bg-[var(--gl-orange)] text-[var(--gl-orange-text)] font-badge text-[10px] px-3 py-1 rounded-full"
            >TRENDING</div>
          )}
          {p.back_in_stock && (
            <div
              data-testid={`home-back-in-stock-${p.slug}`}
              className="bg-[#241C18] text-[var(--gl-orange)] font-badge text-[10px] px-3 py-1 rounded-full"
            >{t("badge.backInStock")}</div>
          )}
        </div>
        <div className="absolute top-3 right-3 z-10">
          <StockBadge product={p} />
        </div>
        <ProgressiveImage
          src={cldImage(p.images?.[0], 800)}
          srcSet={cldSrcSet(p.images?.[0], [400, 600, 800, 1200])}
          sizes="(max-width: 640px) 74vw, (max-width: 1024px) 30vw, 23vw"
          alt={p.name}
          priority={priority}
          position={imgPos(p, p.images?.[0])}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        {/* Floating quick-add button — bottom-right */}
        <button
          data-testid={`home-trending-quickadd-${p.slug}`}
          onClick={quickAdd}
          aria-label={soldOut ? `${p.name} — sold out` : `Add ${p.name} to bag`}
          disabled={soldOut}
          className={`absolute bottom-3 right-3 z-10 h-10 w-10 rounded-full flex items-center justify-center transition-all shadow-md ${
            soldOut
              ? "bg-white/70 text-[#8A7568] cursor-not-allowed"
              : flash
              ? "bg-[#241C18] text-[var(--gl-orange-text)] scale-110"
              : "bg-white/95 text-[#241C18] hover:bg-[var(--gl-orange)] hover:text-[var(--gl-orange-text)] hover:scale-105"
          }`}
        >
          {soldOut ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4l16 16M4 20L20 4"/></svg>
          ) : flash ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12l5 5L20 7"/></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
          )}
        </button>
      </div>

      <div className="mt-3.5 px-1">
        <div className="font-serif-display text-base md:text-lg leading-snug text-[#241C18] min-h-[2.7em]">
          {p.name}
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-3">
          <PriceTag product={p} testid={`home-card-price-${p.slug}`} className="font-serif-display text-lg md:text-xl font-medium text-[#241C18]" />
          <button
            onClick={quickAdd}
            disabled={soldOut}
            data-testid={`home-trending-add-btn-${p.slug}`}
            className={`font-badge text-[10px] tracking-[0.14em] px-3.5 py-2 rounded-full border transition ${
              soldOut
                ? "border-[#8A7568] text-[#8A7568] cursor-not-allowed"
                : "border-[#241C18] hover:bg-[#241C18] hover:text-[var(--gl-orange)]"
            }`}
          >
            {soldOut ? "Sold out" : flash ? "Added ✓" : "Add to bag"}
          </button>
        </div>
      </div>
    </Link>
  );
};

// ─── Hero banner — single still image, floating editorial text ────
// The image URL and object-position come from admin-managed site settings
// (with a hard-coded fallback baked in). Text lives in translations so
// individual banner strings can also be edited from the admin.
const HERO_FALLBACK =
  "https://customer-assets.emergentagent.com/job_lisboa-boutique-ui/artifacts/bam940fn_Outpaint_image_to_Greek_islands_202607111214.webp";

const HeroBanner = () => {
  const { t } = useT();
  const { settings } = useSiteContent();
  const heroSrc = settings?.hero_image_url || HERO_FALLBACK;
  const heroPos = settings?.hero_image_position || "40% 45%";
  const heroPosMobile = settings?.hero_image_position_mobile || "75% 45%";
  const heroScale = Number(settings?.hero_image_scale) || 1.0;
  const heroScaleMobile = Number(settings?.hero_image_scale_mobile) || 1.0;
  return (
    <section
      data-testid={TESTIDS.homeHero}
      className="relative w-full pt-14 md:pt-16"
    >
      <div className="relative w-full overflow-hidden bg-[var(--gl-bg-alt)] aspect-[4/5] sm:aspect-[3/4] md:aspect-[16/9] lg:aspect-[21/9] md:max-h-[86vh]">
        <img
          src={heroSrc}
          alt="GOYA LISBOA"
          className="hero-image absolute inset-0 w-full h-full object-cover"
          style={{
            "--hero-desktop-pos": heroPos,
            "--hero-mobile-pos": heroPosMobile,
            "--hero-desktop-scale": heroScale,
            "--hero-mobile-scale": heroScaleMobile,
          }}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />

        {/* Warm scrim — anchors the copy on the left third without darkening the whole scene */}
        <div className="absolute inset-y-0 left-0 w-full md:w-2/3 bg-gradient-to-r from-[#241C18]/45 via-[#241C18]/15 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#241C18]/40 to-transparent pointer-events-none" />

        {/* Floating editorial copy — Lady Pipa composition, GOYA voice */}
        <div className="absolute inset-x-0 bottom-0 px-6 md:px-14 lg:px-20 pb-10 md:pb-16 lg:pb-20">
          <div className="max-w-2xl">
            <h1
              data-testid="home-hero-title"
              className="font-serif-display leading-[0.88] tracking-[-0.02em] text-[calc(16vw*var(--hts,1))] sm:text-[calc(13vw*var(--hts,1))] md:text-[calc(10vw*var(--hts,1))] lg:text-[calc(9vw*var(--hts,1))]"
              style={{
                "--hts": Number(settings?.hero_title_scale) || 1,
                color: settings?.hero_title_color || "var(--gl-orange)",
                fontWeight: 900,
                textShadow: "0 4px 24px rgba(0,0,0,0.4)",
              }}
            >
              {t("home.banner.title1")}
              <br />
              {t("home.banner.title2")}
            </h1>
            {settings?.hero_subtitle_enabled !== false && (
              <p
                data-testid="home-hero-subtitle"
                className="mt-4 md:mt-6 max-w-md font-badge text-[11px] md:text-sm tracking-[0.22em] uppercase text-[var(--gl-orange)]"
                style={{ textShadow: "0 2px 10px rgba(0,0,0,0.4)" }}
              >
                {t("home.banner.subtitle")}
              </p>
            )}
            <Link
              data-testid={TESTIDS.homeHeroCta}
              to="/collection/all"
              className="inline-block mt-6 md:mt-8 font-badge text-[13px] md:text-sm tracking-[0.28em] underline underline-offset-[8px] decoration-[2px] hover:opacity-80 transition-opacity"
              style={{
                color: settings?.hero_cta_color || "var(--gl-orange)",
                textDecorationColor: settings?.hero_cta_color || "var(--gl-orange)",
                textShadow: "0 2px 10px rgba(0,0,0,0.4)",
              }}
            >
              {t("home.banner.cta")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Category tile — edge-to-edge, elephant-rooms-style ─────────
const CategoryTile = ({ to, img, label, testid, priority = false }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group relative block aspect-[3/4] md:aspect-[4/5] overflow-hidden bg-[var(--gl-bg-alt)]"
  >
    <ProgressiveImage
      src={img}
      alt=""
      priority={priority}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#241C18]/55 via-transparent to-transparent pointer-events-none" />
    <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 text-white">
      <h3 className="font-badge text-base md:text-xl tracking-[0.14em] drop-shadow-md">{label}</h3>
    </div>
  </Link>
);

const Home = () => {
  const { t } = useT();
  const { products, availableCategories, randomProductImages } = useCatalog();
  const scrollerRef = useRef(null);

  // "New In" — admin-curated via the `novidade` checkbox in /admin. Only
  // pieces the boutique has explicitly promoted appear here, preserving
  // the backend's position/created_at order.
  const newIn = useMemo(() => products.filter((p) => p.novidade).slice(0, 12), [products]);

  // Categories with ≥1 in-stock item — everything else is hidden automatically
  const categoryTiles = useMemo(
    () =>
      availableCategories
        .filter((c) => c.image) // must have an image to render a tile
        .map((c) => ({ to: c.to, slug: c.slug, img: c.image, label: c.label })),
    [availableCategories]
  );

  // #GOYAGIRLS lifestyle grid — admin-managed images (site_settings.instagram_gallery)
  // fall back to random product imagery if not enough configured. When the
  // admin has configured images we render ALL of them (no cap) so the grid
  // grows with the community — the fallback stays capped so we don't spam
  // the homepage with random product photos.
  const { settings } = useSiteContent();
  const goyaGirls = useMemo(() => {
    const configured = (settings?.instagram_gallery || []).filter(Boolean);
    if (configured.length >= 3) return configured;
    return randomProductImages(7);
  }, [settings?.instagram_gallery, randomProductImages]);

  // Latest Journal entries — surface up to 3 on the home page.
  const [journalPosts, setJournalPosts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listJournal()
      .then((data) => { if (!cancelled) setJournalPosts((data || []).slice(0, 3)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const nudge = (dir) => {
    if (!scrollerRef.current) return;
    const w = scrollerRef.current.clientWidth * 0.7;
    scrollerRef.current.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  return (
    <div className="pb-24">
      {/* 1 · Banner (hero) — single still image with floating editorial copy */}
      <HeroBanner />

      {/* 2 · New In — horizontal scroll with arrow nudges */}
      {newIn.length > 0 && (
        <section className="pt-14 md:pt-24">
          <div className="flex items-end justify-between px-8 md:px-16 mb-8 md:mb-10">
            <div>
              <h2 data-testid="home-newin-title" className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#241C18]">
                {t("home.trending.title")}
              </h2>
            </div>
            <Link
              to="/collection/new-in"
              className="font-badge text-[10px] md:text-xs text-[#241C18] underline underline-offset-4 whitespace-nowrap"
            >
              {t("home.trending.seeAll")}
            </Link>
          </div>
          <div className="relative">
            <div
              ref={scrollerRef}
              data-testid="home-newin-scroller"
              className="flex gap-5 md:gap-8 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-pl-8 md:scroll-pl-16 pl-8 md:pl-16 pr-8 md:pr-16 pb-8"
            >
              {newIn.map((p, i) => (
                // First 3 are visible without scrolling on most viewports
                <TrendingCard key={p.id} p={p} priority={i < 3} />
              ))}
            </div>
            {/* Desktop arrow nudges */}
            <button
              data-testid="home-newin-arrow-left"
              onClick={() => nudge(-1)}
              aria-label="Scroll left"
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/95 border border-[#F1DCC5] items-center justify-center shadow-md hover:bg-[var(--gl-orange)] hover:text-white transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <button
              data-testid="home-newin-arrow-right"
              onClick={() => nudge(1)}
              aria-label="Scroll right"
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/95 border border-[#F1DCC5] items-center justify-center shadow-md hover:bg-[var(--gl-orange)] hover:text-white transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6"/></svg>
            </button>
          </div>
        </section>
      )}

      {/* 3 · Categories — edge-to-edge grid. Hidden entirely if no categories have stock. */}
      {categoryTiles.length > 0 && (
        <section className="pt-16 md:pt-24">
          {settings?.collection_title_enabled !== false && (
            <div className="mb-8 md:mb-12 text-center px-6">
              <div className="font-badge text-sm md:text-base tracking-[0.32em] text-[var(--gl-orange-hover)]">
                {t("home.categories.eyebrow")}
              </div>
            </div>
          )}
          <div data-testid="home-categories-grid" className="grid grid-cols-2 md:grid-cols-3 gap-0">
            {categoryTiles.map((c, i) => (
              <CategoryTile
                key={c.to}
                to={c.to}
                img={c.img}
                label={c.label}
                priority={i < 3}
                testid={TESTIDS.homeCategoryTile(c.slug)}
              />
            ))}
          </div>
        </section>
      )}

      {/* 4 · #GOYAGIRLS — community lifestyle grid, sourced from actual product imagery. Hidden if empty. */}
      {goyaGirls.length > 0 && (
        <section className="pt-20 md:pt-32">
          <div className="mb-8 md:mb-12 text-center px-6">
            <h2 data-testid="home-goyagirls-title" className="font-serif-display text-4xl md:text-6xl tracking-tight text-[#241C18]">
              {t("home.goyagirls.title")}
            </h2>
            <p className="mt-3 md:mt-4 font-badge text-[11px] md:text-xs text-[#8A7568] tracking-[0.22em]">
              {t("home.goyagirls.sub")}{" "}
              <a
                href="https://www.instagram.com/goyalisboa"
                target="_blank"
                rel="noreferrer"
                data-testid="home-goyagirls-inspiration-link"
                className="underline underline-offset-4 text-[#241C18] hover:text-[var(--gl-orange-hover)]"
              >
                {t("home.goyagirls.link")}
              </a>
            </p>
          </div>
          {/* Mobile: 2-col grid. Desktop: 4-col grid with a hero tile spanning 2x2. Each tile links to @goyalisboa. */}
          <div data-testid="home-goyagirls-grid" className="grid grid-cols-2 md:grid-cols-4 gap-0.5 md:gap-1">
            <a
              href="https://www.instagram.com/goyalisboa"
              target="_blank"
              rel="noreferrer"
              aria-label="Follow @goyalisboa on Instagram"
              className="col-span-2 row-span-2 aspect-square md:aspect-auto overflow-hidden bg-[var(--gl-bg-alt)] group relative block"
            >
              {isVideoUrl(goyaGirls[0]) ? (
                <video
                  src={goyaGirls[0]}
                  muted
                  loop
                  playsInline
                  autoPlay
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <ProgressiveImage src={goyaGirls[0]} alt="#goyagirl" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              )}
              <div className="absolute inset-0 bg-[#241C18]/0 group-hover:bg-[#241C18]/20 transition-colors" />
            </a>
            {goyaGirls.slice(1).map((src, i) => (
              <a
                key={`${src}-${i}`}
                href="https://www.instagram.com/goyalisboa"
                target="_blank"
                rel="noreferrer"
                aria-label="Follow @goyalisboa on Instagram"
                className="aspect-square overflow-hidden bg-[var(--gl-bg-alt)] group relative block"
              >
                {isVideoUrl(src) ? (
                  <video
                    src={src}
                    muted
                    loop
                    playsInline
                    autoPlay
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <ProgressiveImage src={src} alt="#goyagirl" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                )}
                <div className="absolute inset-0 bg-[#241C18]/0 group-hover:bg-[#241C18]/20 transition-colors" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* 4.5 · From the Journal — only renders if entries exist */}
      {journalPosts.length > 0 && (
        <section
          className="mt-24 md:mt-32 px-6 md:px-12"
          data-testid="home-journal-section"
        >
          <div className="flex items-end justify-between mb-8 md:mb-10 max-w-7xl mx-auto">
            <div>
              <div className="font-badge text-[10px] tracking-[0.32em] uppercase text-[var(--gl-orange-hover)]">
                {t("journal.eyebrow")}
              </div>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-2 tracking-tight text-[#241C18]">
                {t("home.journal.title")}
              </h2>
            </div>
            <Link
              to="/journal"
              data-testid="home-journal-view-all"
              className="hidden md:inline-flex items-center font-badge text-[11px] tracking-[0.24em] uppercase text-[#241C18] hover:text-[var(--gl-orange-hover)] transition"
            >
              {t("home.journal.viewAll")} →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 max-w-7xl mx-auto">
            {journalPosts.map((post) => (
              <Link
                key={post.id}
                to={`/journal/${post.slug}`}
                data-testid={`home-journal-card-${post.slug}`}
                className="group block"
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#F9F1E4]">
                  {post.image_url ? (
                    <ProgressiveImage
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#8A7568] text-xs font-badge tracking-widest">
                      {t("journal.eyebrow")}
                    </div>
                  )}
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl mt-4 leading-tight text-[#241C18] group-hover:text-[var(--gl-orange-hover)] transition">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <p className="mt-2 text-sm md:text-base text-[#6B6862] leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                )}
                <span className="mt-4 inline-block font-badge text-[10px] tracking-[0.24em] uppercase text-[var(--gl-orange-hover)]">
                  {t("journal.readOn")} →
                </span>
              </Link>
            ))}
          </div>

          {/* Mobile-only "view all" link — desktop version lives in the section header */}
          <div className="mt-10 text-center md:hidden">
            <Link
              to="/journal"
              data-testid="home-journal-view-all-mobile"
              className="inline-flex items-center font-badge text-[11px] tracking-[0.24em] uppercase text-[#241C18] underline underline-offset-4"
            >
              {t("home.journal.viewAll")} →
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
